import React, { FC, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { i18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import { take } from 'lodash';
import { PropsWithClassName } from '@/types/common';
import Asset from '@/components/Common/Asset';
import TitleBodyGroup from '@/components/Common/TitleBodyGroup';
import StackableCard from '@/components/Common/StackableCard';
import Modal from '@/components/Common/Modal';
import { Lead } from '@/components/Common/Text';
import Card, { CardGrid } from '@/components/Common/Card';
import Button, { ButtonVariant } from '@/components/Common/Button';
import PlusIcon from '@/assets/icons/plus.svg';
import * as styles from './LightingModule.module.scss';

type Props = PropsWithClassName;

const LightingModule: FC<Props> = ({ className }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const data = useStaticQuery<Queries.AllLightingModuleQuery>(graphql`
    query AllLightingModule {
      datoCmsLightingModule {
        text {
          ...DatoCmsTitleBodyGroup
        }

        cards {
          ...DatoCmsCardGroup
        }
      }
    }
  `);

  return (
    <>
      <TitleBodyGroup
        title={
          <hgroup>
            <p className={styles.pretitle}>
              <Trans>Lighting</Trans>
            </p>
            <h2 className={styles.title}>{data.datoCmsLightingModule?.text?.title}</h2>
          </hgroup>
        }
      />
      <TitleBodyGroup title={<></>}>
        {data.datoCmsLightingModule?.text?.body}
      </TitleBodyGroup>

      <CardGrid className={styles.cards}>
        {take(data.datoCmsLightingModule?.cards?.cards, 2).map((card, index) => (
          <Card
            key={card?.id}
            onClick={() => setIsModalOpen(true)}
            image={<Asset asset={card?.asset} />}
            hasAspectRatio
          />
        ))}
      </CardGrid>

      <div className={styles.actions}>
        <Button
          className={styles.viewMore}
          onClick={() => setIsModalOpen(true)}
          variant={ButtonVariant.Ghost}
        >
          <PlusIcon />
          <Trans>View more</Trans>
        </Button>
      </div>

      <Modal
        isOpen={isModalOpen}
        label={i18n._(t`Lighting modal`)}
        onClose={() => setIsModalOpen(false)}
      >
        <div className={styles.modalContent}>
          <Lead className={styles.lead}>
            {data.datoCmsLightingModule?.cards?.text?.title}
          </Lead>
          {data.datoCmsLightingModule?.cards?.cards?.map((card, index) => (
            <StackableCard
              key={card?.id}
              index={index}
              title={card?.title}
              body={card?.body}
              image={<Asset asset={card?.asset} />}
            />
          ))}
        </div>
      </Modal>
    </>
  );
};

export default LightingModule;
